import { FormApi } from "final-form";
import React from "react";
import { Field, Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { useCurrentWorkspace, useCurrentWorkspaceFeatures } from "@dashboard/workspaces/api/helpers";
import { fetchGroups, generateLicenses } from "@devices/store";
import { Box, Theme, Typography } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { SectionCard } from "@sidePanel/shared/SectionCard";
import { useWorkspaceFeature } from "@workspaces/api/helpers";

import { SlotStepperInput } from "./SlotStepperInput";

type FormValues = { grantCount: number };

type GenerateLicensesSectionProps = {
    groupId: number;
    groupName: string;
    groupStatistics: API.Group["statistics"];
};

const useStyles = makeStyles()((theme: Theme) => ({
    workspaceLimitSection: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    progressBarSection: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    progressBarContainer: {
        display: "flex",
        width: "100%",
        height: 12,
        backgroundColor: theme.palette.emteria.border.primary,
        borderRadius: 50,
        overflow: "hidden",
    },
    legend: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    legendDotMuted: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        border: `1px solid ${theme.palette.emteria.border.primary}`,
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
    legendDotAction: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: theme.palette.emteria.action,
    },
    legendDotSuccess: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: theme.palette.emteria.success,
    },
    progressBarSegmentAssigned: {
        height: "100%",
        minWidth: 0,
        transition: "width 0.3s ease",
        backgroundColor: theme.palette.emteria.action,
    },
    progressBarSegmentAvailable: {
        height: "100%",
        minWidth: 0,
        transition: "width 0.3s ease",
        backgroundColor: theme.palette.emteria.success,
    },
    progressBarSegmentFree: {
        height: "100%",
        minWidth: 0,
        transition: "width 0.3s ease",
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
    limitReachedText: { marginTop: theme.spacing(0.5), display: "block" },
    requestAdditionalSlotsLink: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.25),
        "&:hover": { textDecoration: "underline" },
    },
    requestLimitIncreaseLink: {
        color: theme.palette.primary.main,
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" },
    },
    generateFormRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    limitReachedAlert: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        borderRadius: 2,
        border: `1px solid ${theme.palette.emteria.alerts.warning.border}`,
        backgroundColor: theme.palette.emteria.alerts.warning.bg,
        padding: theme.spacing(2),
    },
    limitReachedIcon: {
        color: theme.palette.emteria.warning,
        marginTop: 4,
        flexShrink: 0,
        fontSize: theme.typography.body1.fontSize,
    },
    iconSmall: {
        fontSize: theme.typography.caption.fontSize,
    },
    iconMedium: {
        fontSize: theme.typography.body2.fontSize,
    },
    requestLimitLink: {
        display: "flex",
        justifyContent: "flex-end",
    },
    featuresLoadingBox: {
        minHeight: 160,
        marginTop: 0,
        alignItems: "center",
        justifyContent: "center",
    },
}));

const LimitReachedLink = ({
    children,
    className,
    iconClassName,
}: {
    children?: React.ReactNode;
    className: string;
    iconClassName: string;
}) => (
    <a href="https://emteria.com/p/subscription" target="_blank" rel="noopener noreferrer" className={className}>
        {children}
        <i className={`fa-solid fa-external-link ${iconClassName}`} />
    </a>
);

export const GenerateLicensesSection = ({ groupId, groupName, groupStatistics }: GenerateLicensesSectionProps) => {
    const [isGenerating, setIsGenerating] = React.useState(false);
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();

    const { data: workspace } = useCurrentWorkspace();
    const licenseLimit = useWorkspaceFeature("LicenseLimit");
    const canCreateLicense = PermissionService().workspaceAbility(workspace).can("create", "License");
    const { data: features = [], isLoading: featuresLoading, refetch: refetchWorkspaceFeatures } = useCurrentWorkspaceFeatures();

    const licenseFeature = features.find((f) => f.feature === "LicenseLimit");
    const slotStatus = groupStatistics.slotStatus;
    const assignedCount = slotStatus?.used ?? 0;
    const availableCount = slotStatus?.unused ?? 0;
    const freeSlotsFromGroupStats = slotStatus?.available;
    const freeLicenseSlots =
        freeSlotsFromGroupStats !== undefined ? freeSlotsFromGroupStats : Math.max(0, licenseLimit - (licenseFeature?.used ?? 0));
    const limitForBar = licenseLimit > 0 ? licenseLimit : 1;

    const handleGenerate = async (grantCount: number) => {
        try {
            setIsGenerating(true);
            await dispatch(generateLicenses({ groupId, grantCount })).unwrap();
            if (workspace?.id) {
                await dispatch(fetchGroups(workspace.id)).unwrap();
            }
            await refetchWorkspaceFeatures();
            showSuccessToast(t("sidepanels:groupPanel.toast.licenseGenerated", { name: groupName }));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
            throw err;
        } finally {
            setIsGenerating(false);
        }
    };

    const validate = useValidationSchema(
        z.object({
            grantCount: z.coerce
                .number()
                .int(t("sidepanels:groupPanel.input.grantCount.validation.integer"))
                .min(1, t("sidepanels:groupPanel.input.grantCount.validation.greaterThan"))
                .max(freeLicenseSlots, t("sidepanels:groupPanel.input.grantCount.validation.max", { freeLicenseSlots })),
        }),
    );

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        await handleGenerate(values.grantCount);
        form.restart({ grantCount: 1 });
    };

    return (
        <SectionCard
            title={t("sidepanels:groupPanel.enrollmentTab.generateNewLicenses")}
            description={
                featuresLoading
                    ? undefined
                    : t("sidepanels:groupPanel.enrollmentTab.generateNewLicensesDescription", {
                          slots: licenseLimit,
                      })
            }
            content={
                featuresLoading ? (
                    <LoadingBox className={classes.featuresLoadingBox} />
                ) : (
                    <>
                        <Box className={classes.progressBarSection}>
                            <Box className={classes.progressBarContainer}>
                                <Box
                                    className={classes.progressBarSegmentAssigned}
                                    style={{ width: `${(assignedCount / limitForBar) * 100}%` }}
                                />
                                <Box
                                    className={classes.progressBarSegmentAvailable}
                                    style={{ width: `${(availableCount / limitForBar) * 100}%` }}
                                />
                                <Box
                                    className={classes.progressBarSegmentFree}
                                    style={{ width: `${(freeLicenseSlots / limitForBar) * 100}%` }}
                                />
                            </Box>
                            <Box className={classes.legend}>
                                <Box className={classes.legendItem}>
                                    <Box className={classes.legendDotAction} />
                                    <Typography variant="body2" fontWeight={500} color="text.primary">
                                        {assignedCount} {t("sidepanels:groupPanel.enrollmentTab.usedByDevices")}
                                    </Typography>
                                </Box>
                                <Box className={classes.legendItem}>
                                    <Box className={classes.legendDotSuccess} />
                                    <Typography variant="body2" fontWeight={500} color="text.primary">
                                        {availableCount} {t("sidepanels:groupPanel.enrollmentTab.availableUnused")}
                                    </Typography>
                                </Box>
                                <Box className={classes.legendItem}>
                                    <Box className={classes.legendDotMuted} />
                                    <Typography variant="body2" fontWeight={500} color="text.primary">
                                        {freeLicenseSlots} {t("sidepanels:groupPanel.enrollmentTab.canBeGenerated")}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        {freeLicenseSlots < 1 ? (
                            <Box className={classes.limitReachedAlert}>
                                <i className={`fa-solid fa-circle-info ${classes.limitReachedIcon}`} />
                                <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.primary"
                                    className={classes.limitReachedText}
                                >
                                    <Trans
                                        ns="sidepanels"
                                        i18nKey="groupPanel.enrollmentTab.limitReachedMessage"
                                        values={{ total: licenseLimit }}
                                        components={{
                                            link: (
                                                <LimitReachedLink
                                                    className={classes.requestAdditionalSlotsLink}
                                                    iconClassName={classes.iconSmall}
                                                />
                                            ),
                                        }}
                                    />
                                </Typography>
                            </Box>
                        ) : (
                            <Form<FormValues>
                                onSubmit={onSubmit}
                                validate={validate}
                                initialValues={{ grantCount: 1 }}
                                render={({ handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <Box className={classes.generateFormRow}>
                                            <Field<number> name="grantCount">
                                                {({ input }) => (
                                                    <SlotStepperInput
                                                        value={input.value}
                                                        onChange={input.onChange}
                                                        min={1}
                                                        max={Math.max(1, freeLicenseSlots)}
                                                        slotLabel={t("sidepanels:groupPanel.enrollmentTab.generateSlotsLabel")}
                                                    />
                                                )}
                                            </Field>
                                            <Tooltip
                                                hide={canCreateLicense}
                                                title={t("sidepanels:groupPanel.tooltip.generateNewLicenses")}
                                            >
                                                <PrimaryButton
                                                    type="submit"
                                                    loading={isGenerating}
                                                    disabled={!canCreateLicense || isGenerating || freeLicenseSlots < 1}
                                                >
                                                    {t("common:button.generate")}
                                                </PrimaryButton>
                                            </Tooltip>
                                        </Box>
                                    </form>
                                )}
                            />
                        )}
                    </>
                )
            }
            bottomContent={
                featuresLoading ? null : (
                    <Box className={classes.requestLimitLink}>
                        <Typography
                            variant="body2"
                            component="a"
                            href="https://emteria.com/p/subscription"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.requestLimitIncreaseLink}
                        >
                            {t("sidepanels:groupPanel.enrollmentTab.requestLimitIncrease")}
                            <i className={`fa-solid fa-external-link ${classes.iconMedium}`} />
                        </Typography>
                    </Box>
                )
            }
        />
    );
};
